<template>
    <div class="rules-editor">
        <span v-if="rules.length === 0" class="rules-empty">{{ emptyText }}</span>

        <div v-for="(rule, index) in rules" :key="index" class="rule-row">
            <div class="rule-top">
                <!-- Sin searchable a proposito: eso renderiza un v-combobox, que con
                     no-object muestra el id crudo en vez del nombre de la actividad. -->
                <kimsa-combobox :items="activities" :value="rule.activityId" item-text="name" item-value="id"
                    no-object dense solo with-selected-dot @change="update(index, 'activityId', $event)" />
                <button-tooltip top :tooltip="$t('business.remove_rule')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="rule-remove" v-on="on" v-bind="attrs" @click="remove(index)">
                            <v-icon size="14">mdi-close</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>

            <div class="rule-days">
                <button v-for="day in days" :key="day.value" type="button" class="rule-day"
                    :class="{ 'rule-day--on': rule.days.includes(day.value) }" @click="toggleDay(index, day.value)">
                    {{ day.letter }}
                </button>
            </div>

            <div class="rule-times">
                <kimsa-combobox :items="timeOptions" :value="rule.startTime" item-text="name" item-value="value"
                    no-object dense solo @change="update(index, 'startTime', $event)" />
                <span class="rule-times-sep">{{ $t('business.to_time') }}</span>
                <kimsa-combobox :items="timeOptions" :value="rule.endTime" item-text="name" item-value="value" no-object
                    dense solo @change="update(index, 'endTime', $event)" />
            </div>
        </div>

        <button type="button" class="rule-add" :disabled="activities.length === 0" @click="add">
            <v-icon size="14">mdi-plus</v-icon>
            {{ addLabel }}
        </button>
    </div>
</template>

<script>
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { DAY_END_HOUR, DAY_START_HOUR, TIME_SLOTS, WEEK_DAYS, toTime } from "@/utils/Business";

const DEFAULT_START = toTime(DAY_START_HOUR * 60)

const DEFAULT_END = toTime(DAY_END_HOUR * 60)

export default {
    name: "AccessRulesEditor",
    components: { ButtonTooltip, KimsaCombobox },
    props: {
        rules: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
        emptyText: {
            type: String,
            default: '',
        },
        addLabel: {
            type: String,
            default: '',
        },
    },
    computed: {
        timeOptions() {
            return TIME_SLOTS.map(value => ({ value, name: value }))
        },
        days(vm = this) {
            void vm.$i18n.locale
            return WEEK_DAYS.map(value => ({
                value,
                letter: vm.weekdayLetter(vm.$moment().isoWeekday(value)),
            }))
        },
    },
    methods: {
        emit(rules) {
            this.$emit('change', rules)
        },
        // Se agrega la primera actividad que el plan todavia no use, con todos los dias
        // y horario amplio: es mas facil recortar que armar desde cero.
        add() {
            const vm = this
            const used = vm.rules.map(rule => rule.activityId)
            const activity = vm.activities.find(item => !used.includes(item.id)) || vm.activities[0]
            if (!activity) return
            vm.emit([...vm.rules, {
                activityId: activity.id,
                days: [...WEEK_DAYS],
                startTime: DEFAULT_START,
                endTime: DEFAULT_END,
            }])
        },
        remove(index) {
            this.emit(this.rules.filter((rule, i) => i !== index))
        },
        update(index, field, value) {
            const vm = this
            vm.emit(vm.rules.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)))
        },
        toggleDay(index, day) {
            const vm = this
            const rule = vm.rules[index]
            const days = rule.days.includes(day)
                ? rule.days.filter(item => item !== day)
                : [...rule.days, day].sort((a, b) => a - b)
            vm.update(index, 'days', days)
        },
    },
}
</script>

<style scoped>
.rules-editor {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rules-empty {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.rule-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    background: var(--v-surface2-base);
}

.rule-top {
    display: flex;
    align-items: center;
    gap: 6px;
}

.rule-top > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
}

.rule-remove {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 7px;
    border: 1px solid var(--v-outline-base);
    color: var(--v-error-base);
    transition: border-color 0.15s;
}

.rule-remove:hover {
    border-color: var(--v-error-base);
}

.rule-days {
    display: flex;
    gap: 4px;
}

.rule-day {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    transition: border-color 0.12s, color 0.12s, background 0.12s;
}

.rule-day--on {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
    background: var(--v-surface2-base);
}

.theme--light .rule-day--on {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.rule-times {
    display: flex;
    align-items: center;
    gap: 6px;
}

.rule-times > *:first-child,
.rule-times > *:last-child {
    flex: 1 1 0;
    min-width: 0;
}

.rule-times-sep {
    flex-shrink: 0;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.rule-add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 9px;
    border: 1px dashed var(--v-outline2-base);
    border-radius: 8px;
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtMuted-base);
    transition: color 0.12s, border-color 0.12s;
}

.rule-add:hover:not(:disabled) {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-txtMuted-base);
}

.rule-add:disabled {
    opacity: 0.5;
}
</style>
